/**
 * @file: PresenceHeartbeat.tsx
 *
 * Drives app-presence: heartbeat now + on every foreground, repeat while
 * foregrounded. On background it simply STOPS — the server-side key TTL is
 * the grace window, so a brief background never goes offline and a
 * crash/force-quit ages out identically. There is no explicit "offline"
 * call (see backend docs/user-presence.md).
 *
 * Retry lives here (not in the session provider's `beat`) so the effect
 * cleanup can cancel pending retries: the foreground beat right after a long
 * background can fail (radio not ready on resume), and
 * `TokenRefreshMiddleware` only retries on a 401, not a rejected fetch — so a
 * failed beat is retried with a short backoff (heartbeats are idempotent
 * create-or-refresh). An orphaned retry surviving unmount/account-switch
 * would re-mark a logged-out identity online, so every retry timer is tracked
 * and cleared on teardown.
 */

import { useServiceFactory } from '@euler/services/factory';
import { FC, memo, useCallback, useEffect } from 'react';
import { AppState } from 'react-native';

/** Presence heartbeat cadence while foregrounded (under the 60s server TTL). */
const PRESENCE_HEARTBEAT_MS = 25_000;

export const UserPresenceHeartbeat: FC = memo(() => {
  const { userService } = useServiceFactory();
  // A single heartbeat attempt — an idempotent create-or-refresh.
  const beat = useCallback(
    () => userService.presenceHeartbeat(),
    [userService],
  );

  useEffect(() => {
    const retryTimers = new Set<ReturnType<typeof setTimeout>>();
    const beatWithRetry = (attempt = 0) => {
      beat().catch(e => {
        if (attempt < 2) {
          const t = setTimeout(
            () => {
              retryTimers.delete(t);
              beatWithRetry(attempt + 1);
            },
            (attempt + 1) * 3000,
          );
          retryTimers.add(t);
        } else {
          console.error('[chat-presence] heartbeat failed', e);
        }
      });
    };

    beatWithRetry();
    const startTimer = () =>
      setInterval(() => beatWithRetry(), PRESENCE_HEARTBEAT_MS);
    let timer: ReturnType<typeof setInterval> | null =
      AppState.currentState === 'active' ? startTimer() : null;
    const sub = AppState.addEventListener('change', state => {
      if (state === 'active') {
        beatWithRetry();
        if (timer == null) timer = startTimer();
      } else if (timer != null) {
        clearInterval(timer);
        timer = null;
      }
    });
    return () => {
      sub.remove();
      if (timer != null) clearInterval(timer);
      for (const t of retryTimers) clearTimeout(t);
      retryTimers.clear();
    };
  }, [beat]);
  return null;
});
